import type { FC } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import type { Option } from '@segment/matcha/components/Menu/useMenu'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'

import { useEpisodeQuery } from '@segment/api/routes/goodDataBetterMarketing/queries'

import Layout from '@segment/next-shared/layout'

import LearnFromVeryBest from '../components/LearnFromVeryBest/LearnFromVeryBest'

import Sidebar from './components/Sidebar'
import ArticleContent from './components/ArticleContent'
import Transcript from './components/Transcript'
import RecentEpisodes from './components/RecentEpisodes'
import EpisodePlayers from './components/EpisodePlayers/EpisodePlayers'
import type { HeroProps } from './components/Hero'
import Hero from './components/Hero'

import ogImageJpeg from './assets/OgPodcastHub.jpeg'

export type GoodDataBetterMarketingPodcastProps = {
  episodeSlug: string
}

const GoodDataBetterMarketingPodcast: FC<GoodDataBetterMarketingPodcastProps> = ({ episodeSlug }) => {
  const { data } = useEpisodeQuery({ args: [episodeSlug] })
  if (!data) throw new Error(`Episode data is missing. Make sure it's preloaded`)

  const contentHeadingsOptions = useMemo<Option[]>(
    () => [
      data.speakerBio && {
        text: `Guest Speaker`,
        value: `guest-speaker`,
      },
      data.episodeSummary && {
        text: `Episode Summary`,
        value: `episode-summary`,
      },
      data.keyTakeaways && {
        text: `Key Takeaways`,
        value: `key-takeaways`,
      },
      data.speakerQuotes && {
        text: `Speaker Quotes`,
        value: `speaker-quotes`,
      },
      data.episodeTimestamps && {
        text: `Episode Timestamps`,
        value: `episode-timestamps`,
      },
      data.resourcesLinks && {
        text: `Resources & Links`,
        value: `resources-and-links`,
      },
      data.transcript && {
        text: `Read the Transcript`,
        value: `read-the-transcript`,
      },
    ],
    [
      data.episodeSummary,
      data.episodeTimestamps,
      data.keyTakeaways,
      data.resourcesLinks,
      data.speakerBio,
      data.speakerQuotes,
      data.transcript,
    ]
  )

  const articleSections = useMemo(
    () => [
      {
        heading: `Guest speaker: ${data.speakerName}`,
        content: data.speakerBio,
        id: `guest-speaker`,
      },
      {
        heading: `Episode summary`,
        content: data.episodeSummary,
        id: `episode-summary`,
      },
      {
        heading: `Key takeaways`,
        content: data.keyTakeaways,
        id: `key-takeaways`,
      },
      {
        heading: `Speaker quotes`,
        content: data.speakerQuotes,
        id: `speaker-quotes`,
      },
      {
        heading: `Episode timestamps`,
        content: data.episodeTimestamps,
        id: `episode-timestamps`,
      },
      {
        heading: `Resources & links`,
        content: data.resourcesLinks,
        id: `resources-and-links`,
      },
    ],
    [
      data.episodeSummary,
      data.episodeTimestamps,
      data.keyTakeaways,
      data.resourcesLinks,
      data.speakerBio,
      data.speakerName,
      data.speakerQuotes,
    ]
  )
  const heroData: HeroProps = useMemo(
    () => ({
      episodeNumber: data.episodeNumber,
      episodeTitle: data.episodeTitle,
      episodeDescription: data.episodeDescription,
      episodeImage: {
        src: data.episodeHeroImage?.fields.file.url,
        width: data.episodeHeroImage?.fields.file.details.image?.width,
        height: data.episodeHeroImage?.fields.file.details.image?.height,
        alt: data.episodeHeroImage?.fields.description,
      },
    }),
    [
      data.episodeDescription,
      data.episodeHeroImage?.fields.description,
      data.episodeHeroImage?.fields.file.details.image?.height,
      data.episodeHeroImage?.fields.file.details.image?.width,
      data.episodeHeroImage?.fields.file.url,
      data.episodeNumber,
      data.episodeTitle,
    ]
  )

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout pageTitle={data.socialTitle ?? ``} pageDescription={data.socialDescription} pageOG={ogImageJpeg}>
        <AppearanceProvider appearance="dark">
          <Navigation />
        </AppearanceProvider>
        <Hero {...heroData} />
        <Main>
          <ContentWrapper>
            <SectionBase variant="sidebar" sidebarSize="small" sticky={true}>
              <Section.Sidebar as="aside">
                <Section.Sticky>
                  <Sidebar id="article-sidebar" data={contentHeadingsOptions} />
                </Section.Sticky>
              </Section.Sidebar>
              <Section.Contents>
                <ContentsInner>
                  <EpisodePlayers
                    simplecastId={data.simplecastId}
                    applePodcastsUrl={data.applePodcastsUrl}
                    googlePodcastsUrl={data.googlePodcastsUrl}
                    spotifyUrl={data.spotifyUrl}
                  />
                  <ArticleContent sections={articleSections} />
                  <Transcript heading="Read the transcript" id="read-the-transcript" content={data.transcript} />
                </ContentsInner>
              </Section.Contents>
            </SectionBase>
          </ContentWrapper>
          <RecentEpisodes episodeSlug={episodeSlug} />
          <LearnFromVeryBest />
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const ContentWrapper = styled.div`
  background: ${({ theme }) => theme.palette.neutrals.white[100]};

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-bottom: 40px;
  }
`

const ContentsInner = styled.div`
  display: grid;
  gap: 20px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    gap: 56px;
  }
`

export default GoodDataBetterMarketingPodcast
